import {Record, List} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';

import * as Actions from './actions';

export interface File {
    filePersistenceId: string;
    fileId: string;
    institutionId: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    title: string;
    description: string;
    url: string;
    folderId: string;
    folder: string;
}

export class FileState extends Record<File>({
    filePersistenceId: '',
    fileId: '',
    institutionId: '',
    createdAt: '',
    updatedAt: '',
    deletedAt: null,
    title: '',
    description: '',
    url: '',
    folderId: '',
    folder: '',
}) {}

export interface Folder {
    folderId: string | null;
    path: string;
    title: string;
    parent: Folder | null;
    updatedAt: string;
}

export class FolderState extends Record<Folder>({
    folderId: null,
    path: '',
    title: '',
    parent: null,
    updatedAt: '',
}) {}

export interface DirectoryFolder {
    folderId: string | null;
    title: string;
    folders: List<DirectoryFolder>;
    parent: DirectoryFolder | null;
}

class DirectoryFolderState extends Record<DirectoryFolder>({
    folderId: '',
    title: '',
    folders: List(),
    parent: null,
}) {}

export interface Directory {
    folders: List<DirectoryFolderState>;
    folder: DirectoryFolderState;
}

class DirectoryState extends Record<Directory>({
    folders: List(),
    folder: new DirectoryFolderState(),
}) {}

interface IDocumentsDataState {
    institutionId: string;
    folder: FolderState | null;
    files: List<FileState>;
    folders: List<FolderState>;
    updating: boolean;
    updatingDirectory: boolean;
    showFileUpload: boolean;
    showMoveFile: boolean;
    moveFile: FileState;
    directoryState: DirectoryState;
    showCreateFolder: boolean;
    showRenameFolder: boolean;
    addFolderId: string | null;
    showRenameFile: boolean;
    editFilePersistenceId: string | null;
}

export class DocumentsDataState extends Record<IDocumentsDataState>({
    institutionId: '',
    folder: null,
    files: List(),
    folders: List(),
    updating: true,
    updatingDirectory: true,
    showFileUpload: false,
    showMoveFile: false,
    moveFile: new FileState(),
    directoryState: new DirectoryState(),
    showCreateFolder: false,
    showRenameFolder: false,
    addFolderId: null,
    showRenameFile: false,
    editFilePersistenceId: null,
}) {}

const getParentFolder = folder => {
    if (!folder) {
        return new FolderState();
    }
    return new FolderState({
        ...folder,
        parent: getParentFolder(folder.parent),
    });
};

export default makeReducer<DocumentsDataState>(
    [
        match(Actions.fetchData, state => {
            return state.merge({
                updating: true,
            });
        }),
        match(Actions.fetchDataFailed, state => {
            return state.merge({
                updating: false,
            });
        }),
        match(Actions.fetchDataSuccess, (state, {payload}) => {
            const institutionId = payload.institutionId;

            const files = List(
                payload.result?.files.map(file => {
                    return new FileState(file);
                }),
            );
            const folders = List(
                payload.result?.folders.map(folder => {
                    return new FolderState(folder);
                }),
            );
            const folder = getParentFolder(payload.result?.folder);

            return state.merge({
                updating: false,
                files,
                folders,
                folder,
                institutionId,
            });
        }),
        match(Actions.fetchStructure, state => {
            return state.merge({
                updatingDirectory: true,
            });
        }),
        match(Actions.fetchStructureSuccess, (state, {payload}) => {
            const {result} = payload;

            const folders = List<DirectoryFolderState>(
                result.folders.map(folder => {
                    return new DirectoryFolderState(folder);
                }),
            );

            const folder = getParentFolder(result.folder);

            return state.merge({
                updatingDirectory: false,
                directoryState: state.directoryState.merge({
                    folder: state.directoryState.folder.merge(new DirectoryFolderState(folder)),
                    folders,
                }),
            });
        }),
        match(Actions.fetchStructureFailed, state => {
            return state.merge({
                updatingDirectory: false,
            });
        }),
        match(Actions.toggleFileUpload, (state, {payload}) => {
            return state.merge({
                showFileUpload: payload.show,
            });
        }),
        match(Actions.deleteFileFailed, state => {
            return state.merge({
                updating: false,
            });
        }),
        match(Actions.deleteFileSuccess, (state, {payload}) => {
            const files = state.files.filter(file => file.filePersistenceId !== payload.filePersistenceId);
            return state.merge({
                updating: false,
                files,
            });
        }),
        match(Actions.startMoveFile, (state, {payload}) => {
            return state.merge({
                moveFile: new FileState(payload.file),
                updatingDirectory: true,
                showMoveFile: true,
                directoryState: new DirectoryState(),
            });
        }),
        match(Actions.moveFile, state => {
            return state.merge({
                updatingDirectory: true,
            });
        }),
        match(Actions.moveFileSuccess, state => {
            return state.merge({
                moveFile: new FileState(),
                showMoveFile: false,
            });
        }),
        match(Actions.moveFileFailed, state => {
            return state.merge({
                updatingDirectory: false,
            });
        }),
        match(Actions.moveFileAborted, state => {
            return state.merge({
                showMoveFile: false,
            });
        }),
        match(Actions.showCreateFolder, (state, {payload}) => {
            return state.merge({
                showCreateFolder: payload.show,
                addFolderId: payload.folderId,
            });
        }),
        match(Actions.createFolderSuccess, (state, {payload}) => {
            const newFolder = new FolderState(payload.result.folder);
            const newDirectoryFolder = new DirectoryFolderState(payload.result.folder);

            if (
                newFolder.parent === state.folder?.folderId ||
                (newFolder.parent && state.folder && newFolder.parent.folderId === state.folder.folderId)
            ) {
                return state.merge({
                    showCreateFolder: false,
                    folders: state.folders.push(newFolder),
                    directoryState: state.directoryState.merge({
                        folders: state.directoryState.folders.push(newDirectoryFolder),
                    }),
                });
            }

            return state.merge({
                showCreateFolder: false,
                directoryState: state.directoryState.merge({
                    folders: state.directoryState.folders.push(newDirectoryFolder),
                }),
            });
        }),
        match(Actions.showRenameFolder, (state, {payload}) => {
            return state.merge({
                showRenameFolder: payload.show,
                addFolderId: payload.folderId,
            });
        }),
        match(Actions.renameFolderSuccess, (state, {payload}) => {
            const newFolder = payload.result.folder;

            return state.merge({
                showRenameFolder: false,
                folders: state.folders.map(folder => {
                    if (folder.folderId !== newFolder.folderId) {
                        return folder;
                    }
                    return folder.merge({
                        title: newFolder.title,
                    });
                }),
            });
        }),
        match(Actions.deleteFolderSuccess, (state, {payload}) => {
            const folders = state.folders.filter(folder => folder.folderId !== payload.folderId);

            return state.merge({
                folders,
            });
        }),
        match(Actions.createFileSuccess, (state, {payload}) => {
            const {file} = payload.result;
            const newFile = new FileState(file);

            return state.merge({
                files: state.files.push(newFile),
                showFileUpload: false,
            });
        }),
        match(Actions.showRenameFile, (state, {payload}) => {
            return state.merge({
                showRenameFile: payload.show,
                editFilePersistenceId: payload.filePersistenceId,
            });
        }),
        match(Actions.renameFileSuccess, (state, {payload}) => {
            const newFile = payload.result.file;

            return state.merge({
                showRenameFile: false,
                files: state.files.map(file => {
                    if (file.filePersistenceId !== newFile.filePersistenceId) {
                        return file;
                    }
                    return file.merge({
                        title: newFile.title,
                        description: newFile.description,
                    });
                }),
            });
        }),
    ],
    new DocumentsDataState(),
);
